import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Megaphone, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatError, fileUrl } from "@/lib/api";
import { StatusBadge } from "@/pages/OrdersPage";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { name: "", kind: "AD", placement: "HOME_BANNER", headline: "", image_url: "", link_url: "", budget: 0, status: "PAUSED" };

function CampaignEditor({ camp, onDone }) {
  const isEdit = !!camp?.id;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setF(camp || EMPTY);
  }, [open, camp]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setF((p) => ({ ...p, image_url: data.url }));
      toast.success(data.optimized ? `Creative uploaded & optimized (${Math.round(data.size / 1024)}KB)` : "Creative uploaded");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    try {
      const payload = { ...f, budget: Number(f.budget) || 0 };
      if (isEdit) await api.put(`/admin/campaigns/${camp.id}`, payload);
      else await api.post("/admin/campaigns", payload);
      toast.success(isEdit ? "Campaign updated" : "Campaign created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button data-testid={`edit-campaign-${camp.id}`} variant="outline" size="sm" className="rounded-md"><Pencil className="w-3.5 h-3.5" /></Button>
        ) : (
          <Button data-testid="new-campaign-btn" className="rounded-md bg-slate-900"><Plus className="w-4 h-4 mr-1.5" /> New campaign</Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{isEdit ? "Edit campaign" : "New campaign"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="camp-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Campaign name (internal)"
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="camp-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}
              className="h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 bg-white">
              <option value="AD">Ad campaign</option>
              <option value="SPONSOR">Sponsor campaign</option>
            </select>
            <select data-testid="camp-placement" value={f.placement} onChange={(e) => setF({ ...f, placement: e.target.value })}
              className="h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 bg-white">
              <option value="HOME_BANNER">Homepage banner</option>
              <option value="SEARCH_TOP">Top of search results</option>
            </select>
          </div>
          <input data-testid="camp-headline" value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} placeholder="Headline shown on the banner"
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
          <input data-testid="camp-link" value={f.link_url} onChange={(e) => setF({ ...f, link_url: e.target.value })} placeholder="Click destination URL (e.g. /search?q=shoes or https://…)"
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
          <div>
            <input data-testid="camp-image" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} placeholder="Creative image URL (or upload →)"
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
            <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload creative"}
              <input data-testid="camp-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            </label>
            <p className="text-[10px] text-slate-400 mt-1">Large images auto-optimized · up to 10MB</p>
            {f.image_url && <img src={fileUrl(f.image_url)} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-slate-500">Budget (₹)
              <input data-testid="camp-budget" type="number" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })}
                className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
            </label>
            <label className="text-xs font-semibold text-slate-500">Status
              <select data-testid="camp-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 bg-white">
                <option value="PAUSED">Paused</option>
                <option value="ACTIVE">Active (live on site)</option>
              </select>
            </label>
          </div>
          <Button data-testid="camp-save" onClick={save} disabled={!f.name} className="rounded-md bg-slate-900">{isEdit ? "Save changes" : "Create campaign"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCampaigns() {
  const [camps, setCamps] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/campaigns").then(({ data }) => setCamps(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/campaigns/${id}`);
      toast.success("Campaign deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid="admin-campaigns">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500 flex items-center gap-2"><Megaphone className="w-4 h-4" /> Active campaigns render on the storefront with a Sponsored/Ad label. Impressions and clicks are tracked.</p>
        <CampaignEditor onDone={load} />
      </div>
      <div className="border border-slate-100 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Campaign</TableHead><TableHead>Type</TableHead><TableHead>Placement</TableHead><TableHead>Budget</TableHead><TableHead>Impressions</TableHead><TableHead>Clicks</TableHead><TableHead>CTR</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {camps.map((c) => (
              <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                <TableCell><p className="font-semibold text-sm">{c.name}</p><p className="text-[11px] text-slate-400 line-clamp-1 max-w-[200px]">{c.headline}</p></TableCell>
                <TableCell><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${c.kind === "SPONSOR" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>{c.kind}</span></TableCell>
                <TableCell className="text-xs">{c.placement.replace(/_/g, " ")}</TableCell>
                <TableCell>{inr(c.budget)}</TableCell>
                <TableCell>{c.impressions}</TableCell>
                <TableCell>{c.clicks}</TableCell>
                <TableCell className="font-semibold">{c.impressions ? `${((c.clicks / c.impressions) * 100).toFixed(1)}%` : "—"}</TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell><div className="flex gap-2"><CampaignEditor camp={c} onDone={load} /><Button data-testid={`delete-campaign-${c.id}`} variant="outline" size="sm" className="rounded-md text-red-500" onClick={() => remove(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button></div></TableCell>
              </TableRow>
            ))}
            {!camps.length && <TableRow><TableCell colSpan={9} className="text-center py-16 text-slate-400 text-sm">No campaigns yet. Create an ad or sponsor campaign.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
